import moment from "moment-timezone";
import { useEffect, useRef, useState } from "react";
import DisplayConnection from "./components/Display/DisplayConnection";
import axios from "./config/axios";
import live_display_socket from "./config/live_display_socket";
import { handleAPIError } from "./utils/helpers";

import AnaheimResortTv from "./components/Display/AnaheimResortTv";
import BottomBar from "./components/Display/BottomBar";
import GetStarted from "./components/Display/GetStarted";
import Home from "./components/Display/Home";
import MultiViewLanding from "./components/Display/MultiView/MultiViewLanding";
import ResortTV from "./components/Display/ResortTV";
import ScrollRideWaitTime from "./components/Display/ScrollRideWaitTime";
import Settings from "./components/Display/Settings";
import SingleRideWaitTime from "./components/Display/SingleRideWaitTime";
import Stream from "./components/Display/Stream";
import VacationCountdown from "./components/Display/VacationCountdown";
import VipBoat from "./components/Display/VIP/VipBoat/VipBoat";
import VipCastle from "./components/Display/VIP/VipCastle/VipCastle";
import VipHotel from "./components/Display/VIP/VipHotel/VipHotel";
import VipRoof from "./components/Display/VIP/VipRoof/VipRoof";
import VipSelector from "./components/Display/VIP/VipSelector";
import VipSkyline from "./components/Display/VIP/VipSkyline/VipSkyline";
import VipUpgrade from "./components/Display/VIP/VipUpgrade/VIPUpgrade";
import WifiButton from "./components/Display/WifiButton";
import { useConfig } from "./hooks/useConfig";
import useGlobalStore from "./store/global.store";
import VipPiano from "./components/Display/VIP/VipPiano/VipPiano";
import Utility from "./components/Display/Utility";
import VipSelectorDoor2 from "./components/Display/VIP/VipSelectorDoor2";
import MagicBand from "./components/Display/MagicBand/MagicBand";
import MagicBandTV from "./components/Display/MagicBand/MagicBandTV";
import BeaconConnectSplash from "./components/Display/MagicBand/BeaconConnectSplash";
import { useTV } from "./hooks/useTV";
import BeaconUpgrade from "./components/Display/MagicBand/BeaconUpgrade";
import Dashboard from "./components/Display/Dashboard";
import BroadcastDashboard from "./components/Display/BroadcastDashboard";
import PhotoFrame from "./components/Display/PhotoFrame/PhotoFrame";
import { useFairyFlight } from "./hooks/useFairyFlight";
import AnnouncementPopup from "./components/Display/AnnouncementPopup";
import AnnouncementPopupTV from "./components/Display/AnnouncementPopupTV";
import BeaconComingTV from "./components/Display/BeaconComingTV";
import BeaconComingDisplay from "./components/Display/BeaconComingDisplay";
import BeaconPromoDisplay from "./components/Display/BeaconPromoDisplay";

function FairyFlightRunner() {
  useFairyFlight();
  return null;
}

function App() {
  // we import the config from firebase
  // it ensures that the config is loaded before the app starts
  const { config } = useConfig();
  const { isTv } = useTV();

  const setCurrentView = useGlobalStore((state) => state.setCurrentView);
  const showSettings = useGlobalStore((state) => state.showSettings);
  const device = useGlobalStore((state) => state.device);
  const setDevice = useGlobalStore((state) => state.setDevice);
  const setHasVipAddOn = useGlobalStore((state) => state.setHasVipAddOn);
  const setHasBeaconAddOn = useGlobalStore((state) => state.setHasBeaconAddOn);
  const hasBeaconAddOnLive = useGlobalStore((state) => state.hasBeaconAddOn);
  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  const [deviceID, setDeviceID] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState("active");

  const [countDownDate, setCountDownDate] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const [started, setStarted] = useState(false);
  const [currentRide, setCurrentRide] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [showBeaconSplash, setShowBeaconSplash] = useState(false);
  
  // Render-time flip detection: when the device transitions from
  // no-beacon → beacon while on BAND, set the splash DURING render so
  // it lands in the same paint as the Band+ flip — no flash possible.
  // ref states: null = not yet seen a device, false/true = beacon presence.
  const beaconPresenceRef = useRef(null);
  if (device) {
    const hasBeaconNow = !!device.magic_beacon_device;
    if (
      beaconPresenceRef.current === false &&
      hasBeaconNow &&
      device.screen_view === "BAND" &&
      !showBeaconSplash
    ) {
      setShowBeaconSplash(true);
    }
    beaconPresenceRef.current = hasBeaconNow;
  }

  const getDeviceData = (device_id) => {
    axios
      .get(`/users/device/${device_id}`, { withCredentials: true })
      .then((response) => {
        if (response.data.device_found) {
          // console.log(response.data.device);
          
          
          
          setDevice(response.data.device);
          setHasVipAddOn(response.data.device.has_vip_addon);
          setHasBeaconAddOn(response.data.device.has_beacon_addon);
          if (localStorage.getItem("wsResumeDash")) {
            localStorage.removeItem("wsResumeDash");
            setCurrentView("Dashboard");
          } else {
            setCurrentView(response.data.device.screen_view);
          }
          setSubscriptionStatus(response.data.device.subscription.status);
          // setRefresh(Math.random());
        } else {
          setDevice(null);
          localStorage.removeItem("device_data");
          // localStorage.setItem("memClear", "yes"); // NOTE: This line caused the refresh issue. It shouldn't be here
          window.location.reload();
        }
      })
      .catch((error) => {
        handleAPIError(error);
      })
      .finally(() => {});
  };

  useEffect(() => {
    let joinHeartbeat = null;
    try {
      console.log("v13-fb-config");
      if (localStorage.getItem("memClear")) {
        localStorage.removeItem("memClear");
        setStarted(true);
      }

      const storedVol = parseFloat(localStorage.getItem("volume"));
      if (!(storedVol >= 0 && storedVol <= 1)) {
        localStorage.setItem("volume", 0.3);
      }

      const saved_device_data = JSON.parse(localStorage.getItem("device_data"));
      const dId = saved_device_data._id;

      if (dId) {
        // Register data listeners ONCE, on the socket itself — not inside
        // "connect". Inside-connect registration stacks duplicates on every
        // reconnect and can miss events; off() first makes this idempotent
        // across effect re-runs.
        live_display_socket.off("display_refresh");
        live_display_socket.on("display_refresh", () => {
          console.log("refresh event");
          getDeviceData(dId);
        });

        // Instant beacon pairing push — data arrives in the event itself
        // (room-number pattern): patch device in place, no read, no race.
        live_display_socket.off("beacon_paired");
        live_display_socket.on("beacon_paired", (data) => {
          console.log("beacon_paired push received");
          
          const current = useGlobalStore.getState().device;
          if (current) {
            setDevice({ ...current, magic_beacon_device: data.magic_beacon_device });
          }
          getDeviceData(dId); // background true-up
        });

        // Room join + subscriptions must re-run on every (re)connect, since
        // server-side room membership is lost on disconnect.
        live_display_socket.on("connect", () => {
          console.log("live display socket connected");
          live_display_socket.emit("display_join", { device_id: dId });
          live_display_socket.emit("sub_info_subscribe", { device_id: dId });
        });

        
        // If the socket was already connected before this effect ran
        // (race on app boot), join immediately — "connect" won't re-fire.
        if (live_display_socket.connected) {
          live_display_socket.emit("display_join", { device_id: dId });
          live_display_socket.emit("sub_info_subscribe", { device_id: dId });
        }

        // Room membership heartbeat — reconnects silently drop server-side
        // room membership; re-joining is idempotent and cheap. Guarantees
        // we're in the room within 30s of any reconnect, always.
        joinHeartbeat = setInterval(() => {
          if (live_display_socket.connected) {
            live_display_socket.emit("display_join", { device_id: dId });
          }
        }, 30000);

        live_display_socket.on("sub_info_update", (data) => {
          setHasVipAddOn(data.has_vip_addon);
          setHasBeaconAddOn(data.has_beacon_addon);
          setCountDownDate(data.cf_disney_vacation_countdown);
          setLastName(data.last_name);
          setTimezone(data.cf_select_your_time_zone.split("-")[0]);
          let tz = data.cf_select_your_time_zone.split("-")[0];
          if (tz === "EST" || tz === "EDT") {
            tz = "EST5EDT";
          } else if (tz === "PST" || tz === "PDT") {
            tz = "PST8PDT";
          } else if (tz === "CST") {
            tz = "CST6CDT";
          } else if (tz === "MST") {
            tz = "MST7MDT";
          }
          setTimezone(tz);

          setCurrentDate(moment().tz(tz).format("YYYY-MM-DD"));

          setSubscriptionStatus(data.subscription_status);
        });
      }
    } catch (error) {
      console.log(error);
    }

    return () => {
      if (joinHeartbeat) clearInterval(joinHeartbeat);
      live_display_socket.emit("sub_info_unsubscribe", {
        device_id: deviceID,
      });
    };
  }, [deviceID]);

  useEffect(() => {
    try {
      if (localStorage.getItem("device_data")) {
        // console.log('device data found')
        const saved_device_data = JSON.parse(
          localStorage.getItem("device_data"),
        );
        setDeviceID(saved_device_data._id);
        getDeviceData(saved_device_data._id);
      }
    } catch (error) {
      // console.log(error);
    }
  }, [refresh]);

    // Band+ with no add-on: if this device has a Beacon activation in
  // progress, show the activation status screen instead of the promo.
  const [beaconActivating, setBeaconActivating] = useState(null); // null = checking
  const onBandScreen =
    !!device &&
    (device.screen_view === "BAND" || device.screen_view === "BAND__upgrade");
  useEffect(() => {
    if (!onBandScreen || hasBeaconAddOnLive || !device?._id) return;
    let cancelled = false;
    setBeaconActivating(null);
    axios
      .get("/integrations/beacon-upgrade", { params: { device_id: device._id } })
      .then((res) => {
        if (cancelled) return;
        const s = res.data && res.data.status;
        setBeaconActivating(s === "pending" || s === "preparing" || s === "success");
      })
      .catch(() => {
        if (!cancelled) setBeaconActivating(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBandScreen, hasBeaconAddOnLive, device?._id]);
    // The status screen hides the BottomBar. If the add-on lands while it's
  // showing on BAND, App swaps it out before it can restore the bar.
  useEffect(() => {
    if (hasBeaconAddOnLive && beaconActivating) setShowBottomBar(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBeaconAddOnLive]);
    // RTDB gate: config/Buttons/BeaconPromo (true = everyone sees the promo).
  // When false, only activation codes listed in BeaconPromo_Override see it;
  // everyone else gets the old coming-soon screen.
  const beaconPromoOn = (() => {
    const b = config?.Buttons;
    if (!b) return false;
    if (b.BeaconPromo === true) return true;
    const ov = b.BeaconPromo_Override;
    const id = device?.subscription?._id;
    if (!id || !ov) return false;
    if (Array.isArray(ov)) return ov.includes(id);
    if (typeof ov === "string") return ov === id;
    if (typeof ov === "object") return Object.values(ov).includes(id);
    return false;
  })();
  const beaconPromoOrStatus = !beaconPromoOn ? (
    isTv ? <BeaconComingTV /> : <BeaconComingDisplay />
  ) : beaconActivating === null ? null : beaconActivating ? (
    <BeaconUpgrade device={device} />
  ) : (
    <BeaconPromoDisplay />
  );

  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "hidden" }}>
      <>
        {(!deviceID || !started) && <WifiButton />}

        {!started && deviceID ? (
          <GetStarted setStarted={setStarted} />
        ) : !deviceID ? (
          <DisplayConnection
            setRefresh={setRefresh}
            started={started}
            setStarted={setStarted}
            deviceID={deviceID}
          />
        ) : (
          <>
            {/*<span style={{fontSize: '200px', color: 'black'}}>Connected {localStorage.getItem('device_data') ? 'data found' : 'data not found'}</span>*/}
            {deviceID && device && (
              <>
                {subscriptionStatus === "active" ||
                subscriptionStatus === "non_renewing" ? (
                  <>
                    <BottomBar device={device} />

                    <Utility />
                    <FairyFlightRunner />

                    {device.screen_view === "Home" && (
                      <Home
                        countDownDate={countDownDate}
                        timezone={timezone}
                        currentDate={currentDate}
                        device={device}
                      />
                    )}

                    {device.screen_view === "VIP" && <VipSelector />}
                    {device.screen_view === "VIP_door_2_selector" && (
                      <VipSelectorDoor2 />
                    )}
                    {device.screen_view === "VIP__upgrade" && <VipUpgrade />}
                    {device.screen_view === "VIP__piano" && <VipPiano />}
                    {device.screen_view === "TRC" && <Stream device={device} />}

                    {device.screen_view === "VIP__multiview" && (
                      <MultiViewLanding />
                    )}
                    {device.screen_view === "VIP__skyline" && <VipSkyline />}
                    {device.screen_view === "VIP__roof" && <VipRoof />}

                    {device.screen_view === "VIP__hotel" && <VipHotel />}
                    {device.screen_view === "VIP__castle" && <VipCastle />}
                    {device.screen_view === "VIP__boat" && <VipBoat />}

                    {device.screen_view === "RTV__Orlando" && (
                      <ResortTV device={device} userTz={timezone} />
                    )}

                    {device.screen_view === "RTV__Anaheim" && (
                      <AnaheimResortTv device={device} userTz={timezone} />
                    )}

                    {(device.screen_view === "BAND" || (device.screen_view === "BAND__upgrade" && hasBeaconAddOnLive)) && (
                      !hasBeaconAddOnLive ? (
                        beaconPromoOrStatus
                      ) : isTv ? (
                        <MagicBandTV
                          device={device}
                          onDeviceRefetch={() => getDeviceData(deviceID)}
                        />
                      ) : (
                        <MagicBand
                          device={device}
                          onDeviceRefetch={() => getDeviceData(deviceID)}
                        />
                      )
                    )}

                    {device.screen_view === "BAND__upgrade" && !hasBeaconAddOnLive && (
                      beaconPromoOrStatus
                    )}
                    {device.screen_view === "BAND__activate" && (
                      <BeaconUpgrade device={device} />
                    )}

                    {device.screen_view === "Dashboard" && (
                      <Dashboard 
                        device={device}
                        countDownDate={countDownDate}
                        timezone={timezone}
                        currentDate={currentDate}
                      />
                    )}
                     {device.screen_view === "BroadcastDash" && (
                      <BroadcastDashboard
                        device={device}
                        countDownDate={countDownDate}
                        timezone={timezone}
                        currentDate={currentDate}
                      />
                    )}
                     {device.screen_view === "Photos" && (
                      <PhotoFrame device={device} countDownDate={countDownDate} timezone={timezone} />
                     )}

                    {device.screen_view === "VC" && (
                      <VacationCountdown
                        countDownDate={countDownDate}
                        currentDate={currentDate}
                        timezone={timezone}
                        device={device}
                        last_name={lastName}
                      />
                    )}

                    {device.screen_view === "AutoScroll" && (
                      <ScrollRideWaitTime
                        device={device}
                        setCurrentRide={setCurrentRide}
                      />
                    )}

                    {device.screen_view.includes("Ride__") ||
                    (device.screen_view === "AutoScroll" && currentRide) ? (
                      <SingleRideWaitTime
                        device={device}
                        device_id={deviceID}
                        ride_id={
                          device.screen_view === "AutoScroll"
                            ? currentRide
                            : device.screen_view.split("__")[1]
                        }
                        setCurrentRide={setCurrentRide}
                      />
                    ) : (
                      <span></span>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100vh",
                      fontFamily: "Lato",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "50px",
                        fontWeight: "700",
                      }}
                    >
                      Subscription Inactive
                    </span>
                    <span
                      style={{
                        fontSize: "40px",
                        textAlign: "center",
                        padding: "0 40px",
                      }}
                    >
                      Please visit {subscriptionStatus}{" "}
                      <span style={{ color: "blue" }}>
                        waitsigns.com/portal
                      </span>{" "}
                      to reactivate your subscription or email{" "}
                      <span style={{ color: "blue" }}>
                        support@waitsigns.com
                      </span>{" "}
                      for assistance
                    </span>
                  </div>
                )}
              </>
            )}
          </>
        )}
        {showSettings && <Settings device={device} />}
        {!isTv && started && deviceID && device && <AnnouncementPopup device={device} />}
        {isTv && started && deviceID && device && <AnnouncementPopupTV device={device} />}
        {showBeaconSplash && (
          <BeaconConnectSplash isTv={isTv} onDone={() => setShowBeaconSplash(false)} />
        )}
      </>
    </div>
  );
}

export default App;
